// src/utils/tour/tourSteps.js
//
// The guided walkthroughs, one per page. Each step points at an element by
// its data-tour attribute; steps whose element is not on the page are
// skipped silently, so a tour stays valid when a section is hidden (no
// resume yet, no saved jobs yet).
//
// KEEP IT SHORT. At most five steps a page, one or two plain sentences a
// step. A tour is a nudge toward the right button, not documentation.
//
// VERSIONING IS THE RE-SHOW SWITCH. A tour is shown once per version per
// user. Ship a new feature on a page, bump that tour's version, and everyone
// sees that page's walkthrough again on their next visit - only that page.

const savedJobs = {
  id: "saved-jobs",
  version: 2, // v1: add by link/description, board view, export · v2: fit check
  match: (pathname) => pathname === "/jobs/saved",
  title: "Your job tracker",
  steps: [
    {
      target: '[data-tour="add-job"]',
      title: "Add a job",
      body: "Paste the link, or paste the description. We fill in the rest.",
    },
    {
      target: '[data-tour="fit-chip"]',
      title: "Should you apply?",
      body: "Check my fit reads the posting against your resume: what you already have, what is missing, what to lead with.",
    },
    {
      target: '[data-tour="active-job"]',
      title: "The job you are working on",
      body: "Tailor, Cover Letter and Interview Prep all use this job. Change it from any card.",
    },
    {
      target: '[data-tour="view-toggle"]',
      title: "List or Board",
      body: "List to work one job. Board to see the whole pipeline.",
    },
    {
      target: '[data-tour="job-status"]',
      title: "Move a job along",
      body: "Marking Applied asks which resume you sent, so you can look it up later.",
    },
    {
      target: '[data-tour="job-actions"]',
      title: "Do the work for this job",
      body: "Tailor the resume, write the cover letter, prep the interview - each opens with this job selected.",
    },
  ],
};

const findJobs = {
  id: "find-jobs",
  version: 1, // v1: recommended roles, filters, board links
  match: (pathname) => pathname === "/jobs",
  title: "Finding jobs",
  steps: [
    {
      target: '[data-tour="recommended-roles"]',
      title: "Titles your resume fits",
      body: "Read from your resume. Tap one, or type your own below.",
    },
    {
      target: '[data-tour="board-links"]',
      title: "Search the boards",
      body: "Each opens a live search with your filters applied. Found one? Save it in your tracker.",
    },
  ],
};

const resumeEditor = {
  id: "resume-editor",
  version: 1, // v1: command center, tailor by link / paste
  match: (pathname) => pathname === "/create-resume/resume",
  title: "Your resume",
  steps: [
    {
      target: '[data-tour="command-center"]',
      title: "Format, Tailor, Export",
      body: "Template and layout, tailoring to a job, and downloads - one tab each.",
    },
    {
      target: '[data-tour="tailor-url-fetcher"]',
      title: "Tailor to a job",
      body: "Paste the posting's link, or the description itself. Your main resume is never changed - you get a separate version.",
    },
  ],
};

const coverLetter = {
  id: "cover-letter",
  version: 1, // v1: selected job targets the letter
  match: (pathname) => pathname === "/cover-letter",
  title: "Cover letter",
  steps: [
    {
      target: '[data-tour="active-job"]',
      title: "Written for this job",
      body: "The letter targets your selected job. Pick another from Saved Jobs.",
    },
    {
      target: '[data-tour="cl-form"]',
      title: "Answer one or two questions",
      body: "Your details come from your resume. Say a little about the role, then Generate.",
    },
  ],
};

const interview = {
  id: "interview",
  version: 1, // v1: per-job sessions, improve answer with feedback
  match: (pathname) => pathname === "/interview",
  title: "Interview prep",
  steps: [
    {
      target: '[data-tour="interview-sessions"]',
      title: "One session per job",
      body: "Switch between the jobs you are prepping for. General is for practice.",
    },
    {
      target: '[data-tour="interview-form"]',
      title: "Generate questions",
      body: "Position and description are filled from your selected job. Your resume shapes the answers.",
    },
    {
      target: '[data-tour="interview-results"]',
      title: "Make each answer yours",
      body: "Save the good ones. Click Improve answer and say what to change.",
    },
  ],
};

const answers = {
  id: "answers",
  version: 1, // v1: prefilled from the selected job
  match: (pathname) => pathname === "/applications/answers",
  title: "Application answers",
  steps: [
    {
      target: '[data-tour="answers-list"]',
      title: "Write once, paste anywhere",
      body: "The questions every form asks. Draft the tricky ones with AI, then copy into any application.",
    },
  ],
};

export const PAGE_TOURS = [
  savedJobs,
  findJobs,
  resumeEditor,
  coverLetter,
  interview,
  answers,
];

/** The page tour for a path, or null. */
export const findTourForPath = (pathname) =>
  PAGE_TOURS.find((tour) => tour.match(String(pathname || ""))) || null;

export default { PAGE_TOURS, findTourForPath };
